import { useState, useEffect, useRef, useCallback } from "react";
import type { Node, Edge } from "@xyflow/react";
import type ELKType from "elkjs/lib/elk.bundled.js";
import type { DiagramSpec, LayoutOptions } from "./types";
import {
  specToElk,
  nodeSizeMapFingerprint,
  type NodeSizeMap,
} from "./spec-to-elk";
import { elkToFlow } from "./elk-to-flow";
import { structuralFingerprint } from "./structural-fingerprint";
import { mergeStyles, mergeEdgeStyles } from "./merge-styles";

/** Lazy-loaded ELK singleton, bundled (self-hosted) as its own lazy chunk. */
let elkInstance: InstanceType<typeof ELKType> | null = null;
async function getElk(): Promise<InstanceType<typeof ELKType>> {
  if (!elkInstance) {
    const { default: ELK } = await import("elkjs/lib/elk.bundled.js");
    elkInstance = new ELK();
  }
  return elkInstance;
}

export interface AutoLayoutResult {
  nodes: Node[];
  edges: Edge[];
  layoutReady: boolean;
}

/**
 * React hook that manages automatic ELK layout for a DiagramSpec.
 *
 * Re-runs layout only when the graph structure changes (nodes added/
 * removed, edges changed, parent assignments changed). When only
 * styles or data change, merges the new values onto existing positions
 * without invoking ELK — keeping layout stable and avoiding expensive
 * recomputation.
 *
 * Node sizing works via a CSS override that prevents React Flow from
 * constraining wrapper divs (see diagram-view.css). This lets content
 * determine the actual size. The measurement cycle in DiagramRenderer
 * captures DOM dimensions and feeds them back here, triggering a
 * re-layout with accurate sizes.
 */
export function useAutoLayout(
  spec: DiagramSpec,
  opts?: LayoutOptions,
  nodeSizes?: NodeSizeMap,
): AutoLayoutResult {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [layoutReady, setLayoutReady] = useState(false);

  const prevFingerprintRef = useRef("");
  const prevOptsRef = useRef<LayoutOptions | undefined>(undefined);
  const prevSizesFpRef = useRef("");
  const prevNodesRef = useRef<Node[]>([]);
  const prevEdgesRef = useRef<Edge[]>([]);
  const prevSpecEdgesRef = useRef<readonly import("./types").DiagramEdge[]>([]);
  const layoutVersionRef = useRef(0);

  const runLayout = useCallback(
    async (
      currentSpec: DiagramSpec,
      currentOpts: LayoutOptions | undefined,
      version: number,
      sizes?: NodeSizeMap,
    ) => {
      const elkGraph = specToElk(currentSpec, currentOpts, sizes);
      const elk = await getElk();
      const laidOut = await elk.layout(elkGraph);
      if (layoutVersionRef.current !== version) return;

      const result = elkToFlow(laidOut, currentSpec);
      setNodes(result.nodes);
      setEdges(result.edges);
      prevNodesRef.current = result.nodes;
      prevEdgesRef.current = result.edges;
      setLayoutReady(true);
    },
    [],
  );

  useEffect(() => {
    const fp = structuralFingerprint(spec);
    const sizesFp = nodeSizeMapFingerprint(nodeSizes);
    const optsChanged = opts !== prevOptsRef.current;
    const sizesChanged = sizesFp !== prevSizesFpRef.current;
    prevOptsRef.current = opts;
    prevSizesFpRef.current = sizesFp;

    const structureChanged = fp !== prevFingerprintRef.current;
    if (structureChanged || optsChanged || sizesChanged) {
      prevFingerprintRef.current = fp;
      // `layoutReady` is deliberately NOT cleared here. It gates the
      // "Computing layout..." placeholder, and that placeholder replaces the
      // whole React Flow canvas — so clearing it on every structural change
      // (a simulation spawning a node mid-run) would unmount React Flow,
      // resetting the viewport, re-running fitView and discarding every DOM
      // measurement the layout depends on. The previous layout stays on
      // screen for the few milliseconds ELK needs instead.
      const version = ++layoutVersionRef.current;
      // A rejection here (ELK's lazy chunk failing to load, most plausibly)
      // leaves the previous layout on screen — or, before the first layout,
      // the "Computing layout..." placeholder. Surface it rather than
      // letting it surface as an unhandled promise rejection.
      void runLayout(spec, opts, version, nodeSizes).catch((err: unknown) => {
        console.error("Diagram layout failed", err);
      });
    } else if (prevNodesRef.current.length > 0) {
      const merged = mergeStyles(prevNodesRef.current, spec);
      setNodes(merged);
      prevNodesRef.current = merged;
      // Only merge edges when the spec's edge array actually changed
      // (topology update). On metrics-only frames, edges are unchanged.
      const specEdgesChanged = spec.edges !== prevSpecEdgesRef.current;
      if (specEdgesChanged) {
        prevSpecEdgesRef.current = spec.edges;
        if (prevEdgesRef.current.length > 0) {
          const mergedEdges = mergeEdgeStyles(prevEdgesRef.current, spec);
          setEdges(mergedEdges);
          prevEdgesRef.current = mergedEdges;
        }
      }
    }
  }, [spec, opts, nodeSizes, runLayout]);

  return { nodes, edges, layoutReady };
}
